import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { ArrowRightLeft, Snowflake, Wallet } from "lucide-react";
import duckMoneyGif from "../../../assets/utya-money.gif";
import duckPremiumStarsGif from "../../../assets/utya-premium-stars.gif";
import { getTelegramUser, openTelegramLink } from "../../../lib/tg";
import { useVisibleRefresh } from "../../../lib/useVisibleRefresh";
import {
  driverApi,
  type ConversionPreview,
  type DriverWallet,
} from "../lib/api";
import {
  displayMoney,
  exactMoney,
  moneyUnits,
  normalizedMoney,
} from "../lib/walletMoney";
import {
  CoinsIcon,
  DriverApiKeyBlock,
  DriverBalance,
  HistoryEntryButton,
  RubIcon,
  UsdtIcon,
  WithdrawHeader,
} from "./Balance";
import {
  DriverWalletHistory,
  type WalletOperation,
} from "./DriverWalletHistory";
type Mode = "balance" | "convert" | "withdraw" | "history";
const MANAGER_LINK = "https://t.me/m/zuEpyK4gOTY6";

function errorText(error: unknown, fallback: string): string {
  const raw = error as {
    detail?: unknown;
    error?: { message?: string; code?: string };
    code?: string;
  } | null;
  const code = raw?.error?.code ?? raw?.code;
  if (code === "rate_changed" || raw?.detail === "rate_changed")
    return "курс изменился — проверьте новые условия и подтвердите ещё раз";
  if (code === "insufficient_balance")
    return "доступная сумма изменилась — проверьте баланс";
  if (typeof raw?.error?.message === "string") return raw.error.message;
  return typeof raw?.detail === "string" &&
    !/validation|input should|internal error/i.test(raw.detail)
    ? raw.detail
    : fallback;
}
function dateText(value: string) {
  return `${new Date(value).toLocaleString("ru-RU", { timeZone: "Europe/Moscow" })} мск`;
}

export function DriverWalletPage({ onBack }: { onBack: () => void }) {
  const [wallet, setWallet] = useState<DriverWallet | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const live = useRef(false);
  const inFlight = useRef<Promise<void> | null>(null);
  const refresh = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const request = (async () => {
      try {
        const response = await driverApi.getWallet();
        if (!response.data || response.error)
          throw new Error("wallet_load_failed");
        if (live.current) {
          setWallet(response.data);
          setLoadError(null);
        }
      } catch {
        if (live.current)
          setLoadError("не удалось обновить баланс — повторите загрузку");
        throw new Error("wallet_load_failed");
      }
    })().finally(() => {
      inFlight.current = null;
    });
    inFlight.current = request;
    return request;
  }, []);
  useEffect(() => {
    live.current = true;
    void refresh().catch(() => {});
    return () => {
      live.current = false;
    };
  }, [refresh]);
  useVisibleRefresh(refresh);
  if (!wallet)
    return (
      <div className="tp-screen">
        {loadError ? (
          <>
            <p className="tp-alert" role="alert">
              {loadError}
            </p>
            <button
              className="tp-button-primary"
              onClick={() => void refresh().catch(() => {})}
            >
              повторить
            </button>
          </>
        ) : (
          <p>загрузка...</p>
        )}
      </div>
    );
  if (!wallet.wallet_enabled) return <DriverBalance onBack={onBack} />;
  return <UsdtWallet wallet={wallet} refresh={refresh} loadError={loadError} />;
}

function UsdtWallet({
  wallet,
  refresh,
  loadError,
}: {
  wallet: DriverWallet;
  refresh: () => Promise<void>;
  loadError: string | null;
}) {
  const [mode, setMode] = useState<Mode>("balance");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"crypto_check" | "support" | null>(null);
  const [preview, setPreview] = useState<ConversionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [successAmount, setSuccessAmount] = useState("");
  const [manager, setManager] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [operations, setOperations] = useState<WalletOperation[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cancelId, setCancelId] = useState<number | null>(null);
  const previewVersion = useRef(0);
  const normalized = normalizedMoney(amount);
  const entered = normalized === null ? null : moneyUnits(normalized);
  const pending = moneyUnits(wallet.pending_conversion_rub) ?? 0n;
  const available = moneyUnits(wallet.available_usdt) ?? 0n;

  const loadHistory = useCallback(async (offset = 0) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await driverApi.listOperations({ limit: 50, offset });
      if (response.error || !response.data) throw new Error("history_failed");
      const rows = response.data as WalletOperation[];
      setOperations((current) =>
        offset
          ? [
              ...current,
              ...rows.filter(
                (row) => !current.some((existing) => existing.id === row.id),
              ),
            ]
          : rows,
      );
      setHasMore(rows.length === 50);
    } catch {
      setHistoryError("не удалось загрузить историю");
    } finally {
      setHistoryLoading(false);
    }
  }, []);
  useEffect(() => {
    if (mode === "history") void loadHistory();
  }, [mode, loadHistory]);

  useEffect(() => {
    const version = ++previewVersion.current;
    setPreview(null);
    setPreviewLoading(false);
    if (
      mode !== "convert" ||
      !wallet.confirmed_rate ||
      !normalized ||
      entered === null ||
      entered <= 0n ||
      entered > pending
    )
      return;
    setPreviewLoading(true);
    const timer = window.setTimeout(() => {
      void driverApi
        .previewConversion(normalized)
        .then((response) => {
          if (version !== previewVersion.current) return;
          if (response.error || !response.data) {
            setError(
              errorText(response.error, "не удалось рассчитать конвертацию"),
            );
            return;
          }
          setPreview(response.data);
        })
        .catch(() => {
          if (version === previewVersion.current)
            setError(
              "не удалось рассчитать конвертацию — повторите ввод суммы",
            );
        })
        .finally(() => {
          if (version === previewVersion.current) setPreviewLoading(false);
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      ++previewVersion.current;
    };
  }, [
    mode,
    normalized,
    entered,
    pending,
    wallet.confirmed_rate?.id,
    previewAttempt,
  ]);

  function open(next: Mode) {
    setMode(next);
    setAmount("");
    setError(null);
    setSuccess(null);
    setSuccessAmount("");
    setMethod(null);
    setManager(false);
    setPreview(null);
    setCancelId(null);
  }
  async function submit() {
    if (busyRef.current) return;
    if (mode === "withdraw" && !method) return;
    if (entered === null || entered <= 0n || !normalized) {
      setError("введите положительную сумму, не более 8 знаков после запятой");
      return;
    }
    if (
      (mode === "convert" && entered > pending) ||
      (mode === "withdraw" && entered > available)
    ) {
      setError("сумма превышает доступный баланс");
      return;
    }
    if (mode === "withdraw" && entered < 100000000n) {
      setError("минимальная сумма вывода — 1 USDT");
      return;
    }
    if (
      mode === "convert" &&
      (!preview ||
        (preview.amount_rub !== normalized &&
          exactMoney(preview.amount_rub) !== normalized))
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const response =
        mode === "convert"
          ? await driverApi.convert({
              amount_rub: normalized,
              confirmed_rate_id: preview!.confirmed_rate_id,
            })
          : await driverApi.createUsdtWithdrawal({
              amount_usdt: normalized,
              method: method!,
              wallet_address:
                method === "crypto_check"
                  ? `telegram:${getTelegramUser()?.id ?? "self"}`
                  : "manager:@Pinkpko",
            });
      if (response.error) {
        setError(
          errorText(
            response.error,
            "операция не выполнена — повторите попытку",
          ),
        );
        setPreview(null);
        await refresh().catch(() => {});
        return;
      }
      const result = response.data as {
        amount_usdt?: string;
        payout_amount_usdt?: string;
      };
      setSuccessAmount(
        result.amount_usdt ?? result.payout_amount_usdt ?? normalized,
      );
      setSuccess(
        mode === "convert"
          ? `зачислено ${displayMoney(result.amount_usdt ?? preview!.amount_usdt)} USDT на основной баланс`
          : `заявка на вывод ${displayMoney(result.amount_usdt ?? result.payout_amount_usdt ?? normalized)} USDT создана`,
      );
      setManager(mode === "withdraw" && method === "support");
      setAmount("");
      setPreview(null);
      await refresh().catch(() => {
        setError(
          "операция выполнена, но баланс не обновился — повторите загрузку",
        );
      });
    } catch {
      setError(
        "ответ не получен — повторите с той же суммой, повторное списание исключено",
      );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }
  async function cancelWithdrawal(id: number) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await driverApi.cancelWithdrawal(id);
      if (response.error)
        setError(errorText(response.error, "не удалось отменить заявку"));
      else setCancelId(null);
      await Promise.all([refresh().catch(() => {}), loadHistory()]);
    } catch {
      setError("ответ не получен — обновите историю и повторите отмену");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }
  const converting = mode === "convert";
  const shownRate =
    preview?.rate_rub_per_usdt ?? wallet.confirmed_rate?.rate_rub_per_usdt;
  const confirmedAt =
    preview?.confirmed_at ?? wallet.confirmed_rate?.confirmed_at;
  const formError =
    error ??
    (amount && entered === null
      ? "введите число, не более 8 знаков после запятой"
      : entered !== null && entered > (converting ? pending : available)
        ? "сумма превышает доступный баланс"
        : null);
  const heading = converting
    ? "конвертация в USDT"
    : mode === "withdraw"
      ? manager
        ? "вывод через менеджера"
        : "вывод средств"
      : "история транзакций";

  function fillMax() {
    setAmount(
      exactMoney(
        converting ? wallet.pending_conversion_rub : wallet.available_usdt,
      ) ?? "",
    );
    setError(null);
  }

  return (
    <div
      className={
        mode === "balance"
          ? "tp-screen tp-wallet-screen tp-balance-screen"
          : mode === "history"
            ? "tp-screen tp-wallet-screen tp-balance-history-screen"
            : "tp-screen tp-wallet-screen tp-withdraw-screen"
      }
    >
      {mode === "balance" ? (
        <div className="tp-balance-hero">
          <img
            src={duckMoneyGif}
            alt=""
            className="tp-balance-duck"
            draggable={false}
          />
          <h1 className="tp-driver-stats-title">управление финансами</h1>
        </div>
      ) : (
        <WithdrawHeader
          title={heading}
          onBack={() => open("balance")}
          disabled={busy}
        />
      )}
      {loadError && (
        <p role="alert" className="tp-alert">
          {loadError}
        </p>
      )}
      {(mode === "balance" || mode === "history" || success) && error && (
        <p role="alert" className="tp-alert">
          {error}
        </p>
      )}
      {mode === "balance" && (
        <>
          <div className="tp-balance-card">
            {[
              [
                "основной баланс:",
                wallet.available_usdt,
                "USDT",
                "balance-available",
              ],
              [
                "замороженный баланс:",
                wallet.frozen_rub,
                "₽",
                "balance-frozen",
              ],
              [
                "ожидает конвертации:",
                wallet.pending_conversion_rub,
                "₽",
                "balance-conversion-pending",
              ],
              [
                "в обработке на вывод:",
                wallet.withdrawal_pending_usdt,
                "USDT",
                "balance-withdrawal-pending",
              ],
            ].map(([label, value, currency, testId], index) => (
              <Fragment key={label}>
                <div className="tp-balance-row">
                  <div className="tp-balance-row-label">
                    {index === 0 ? (
                      <Wallet size={18} strokeWidth={2} />
                    ) : index === 2 ? (
                      <ArrowRightLeft size={18} strokeWidth={2} />
                    ) : (
                      <Snowflake size={18} strokeWidth={2} />
                    )}
                    <span>{label}</span>
                  </div>
                  <span className="tp-balance-value" data-testid={testId}>
                    {displayMoney(value, true)} {currency}
                  </span>
                </div>
                <div className="tp-balance-divider" />
              </Fragment>
            ))}
            <button
              type="button"
              className="tp-button-primary tp-balance-withdraw-button"
              onClick={() => open("convert")}
            >
              <ArrowRightLeft />
              <span>конвертировать в USDT</span>
            </button>
            <button
              type="button"
              className="tp-button-primary tp-balance-withdraw-button"
              onClick={() => open("withdraw")}
            >
              <CoinsIcon />
              <span>сделать вывод</span>
            </button>
          </div>
          {(moneyUnits(wallet.legacy_withdrawal_pending_rub) ?? 0n) > 0n && (
            <p className="tp-withdraw-course-note">
              старые заявки на вывод:{" "}
              {displayMoney(wallet.legacy_withdrawal_pending_rub, true)} ₽ ·
              подробности в истории
            </p>
          )}
          <HistoryEntryButton onOpen={() => open("history")} />
          <DriverApiKeyBlock />
        </>
      )}
      {(mode === "convert" || mode === "withdraw") &&
        (success ? (
          manager ? (
            <>
              <div className="tp-withdraw-manager-card">
                <div className="tp-withdraw-manager-row">
                  <span>сумма вывода</span>
                  <strong>{displayMoney(successAmount)} USDT</strong>
                </div>
                <div className="tp-withdraw-manager-row">
                  <span>способ вывода</span>
                  <strong>через менеджера</strong>
                </div>
                <p className="tp-withdraw-course-note" role="status">
                  {success}
                </p>
              </div>
              <button
                type="button"
                className="tp-button-primary tp-withdraw-manager-button w-full"
                onClick={() => openTelegramLink(MANAGER_LINK)}
              >
                открыть менеджера
              </button>
            </>
          ) : (
            <div className="tp-withdraw-success">
              <img
                src={duckPremiumStarsGif}
                alt=""
                className="tp-withdraw-success-duck"
                draggable={false}
              />
              <p role="status">{success}</p>
            </div>
          )
        ) : (
          <>
            {mode === "withdraw" && (
              <div className="tp-withdraw-methods">
                <button
                  type="button"
                  className="tp-withdraw-method"
                  data-active={method === "crypto_check"}
                  disabled={busy}
                  onClick={() => {
                    setMethod("crypto_check");
                    setError(null);
                  }}
                >
                  вывести без комиссии (в Crypto Bot)
                </button>
                <button
                  type="button"
                  className="tp-withdraw-method"
                  data-active={method === "support"}
                  disabled={busy}
                  onClick={() => {
                    setMethod("support");
                    setError(null);
                  }}
                >
                  вывод через менеджера
                </button>
              </div>
            )}
            {(converting || method) && (
              <div className="tp-withdraw-conversion">
                <div className="tp-withdraw-conversion-card">
                  <div className="tp-withdraw-conversion-row">
                    <div className="tp-withdraw-currency-block">
                      <span className="tp-withdraw-row-caption">
                        {converting ? "вы конвертируете:" : "сумма вывода:"}
                      </span>
                      <div className="tp-withdraw-currency-label">
                        {converting ? <RubIcon /> : <UsdtIcon />}
                        <span>{converting ? "RUB" : "USDT"}</span>
                      </div>
                    </div>
                    <div className="tp-withdraw-amount-column">
                      <div className="tp-withdraw-inline-balance">
                        <span>
                          баланс:{" "}
                          {displayMoney(
                            converting
                              ? wallet.pending_conversion_rub
                              : wallet.available_usdt,
                            true,
                          )}{" "}
                          {converting ? "₽" : "USDT"}
                        </span>
                        <button
                          type="button"
                          className="tp-withdraw-max-button"
                          disabled={busy}
                          onClick={fillMax}
                        >
                          <span
                            className="tp-withdraw-max-separator"
                            aria-hidden="true"
                          >
                            •
                          </span>
                          <span>макс</span>
                        </button>
                      </div>
                      <div className="tp-withdraw-balance-value">
                        <input
                          aria-label={converting ? "сумма RUB" : "сумма USDT"}
                          className="tp-withdraw-amount-input"
                          inputMode="decimal"
                          autoComplete="off"
                          value={amount}
                          disabled={busy}
                          placeholder="0"
                          onChange={(event) => {
                            setAmount(event.target.value);
                            setError(null);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  {converting ? (
                    <>
                      <div className="tp-withdraw-divider" />
                      <div className="tp-withdraw-conversion-row tp-withdraw-receive-row">
                        <div className="tp-withdraw-currency-block">
                          <span className="tp-withdraw-row-caption">
                            вы получаете:
                          </span>
                          <div className="tp-withdraw-currency-label">
                            <UsdtIcon />
                            <span>USDT</span>
                          </div>
                        </div>
                        <div className="tp-withdraw-amount-column">
                          <div className="tp-withdraw-balance-value">
                            <output
                              aria-label="будет зачислено USDT"
                              className="tp-withdraw-calculated-value tp-wallet-conversion-result"
                              aria-busy={previewLoading}
                            >
                              {preview
                                ? displayMoney(preview.amount_usdt)
                                : "0"}
                            </output>
                          </div>
                        </div>
                      </div>
                      <div className="tp-withdraw-divider" />
                      <div className="tp-withdraw-course tp-withdraw-course--compact">
                        курс:{" "}
                        {shownRate ? displayMoney(shownRate) + " ₽/USDT" : "—"}
                      </div>
                      <div className="tp-withdraw-course-note">
                        {confirmedAt ? (
                          <>
                            подтверждён {dateText(confirmedAt)}
                            <br />
                            USDT зачисляются на основной баланс
                          </>
                        ) : (
                          "конвертация станет доступна после первого подтверждения курса администратором"
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="tp-withdraw-course-note">
                      минимальная сумма — 1 USDT
                    </p>
                  )}
                </div>
                {formError && (
                  <p role="alert" className="tp-withdraw-form-error">
                    {formError}
                  </p>
                )}
                {converting && previewLoading && (
                  <p role="status" className="tp-withdraw-available-note">
                    рассчитываем...
                  </p>
                )}
                {converting &&
                  !previewLoading &&
                  !preview &&
                  entered !== null &&
                  entered > 0n &&
                  entered <= pending &&
                  wallet.confirmed_rate && (
                    <div className="tp-withdraw-refresh-row">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setError(null);
                          setPreviewAttempt((value) => value + 1);
                        }}
                      >
                        повторить расчёт
                      </button>
                    </div>
                  )}
                <button
                  type="button"
                  className="tp-button-primary tp-withdraw-request-button w-full"
                  disabled={
                    busy || (converting && (previewLoading || !preview))
                  }
                  aria-busy={busy}
                  onClick={() => void submit()}
                >
                  {converting ? <ArrowRightLeft /> : <CoinsIcon />}
                  <span>
                    {converting
                      ? busy
                        ? "конвертируем..."
                        : "конвертировать в USDT"
                      : busy
                        ? "создаём..."
                        : "запросить вывод"}
                  </span>
                </button>
              </div>
            )}
          </>
        ))}
      {mode === "history" && (
        <DriverWalletHistory
          operations={operations}
          loading={historyLoading}
          error={historyError}
          hasMore={hasMore}
          busy={busy}
          cancelId={cancelId}
          onRefresh={() => void loadHistory()}
          onLoadMore={() => void loadHistory(operations.length)}
          onAskCancel={setCancelId}
          onDismissCancel={() => setCancelId(null)}
          onConfirmCancel={(id) => void cancelWithdrawal(id)}
        />
      )}
    </div>
  );
}
